import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Input, InputNumber, Segmented, Select, Tag } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus,
  faUserMinus,
  faRightLeft,
  faBasketball,
  faUserSlash,
  faCheckDouble,
  faFloppyDisk,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import {
  BASKET_TEAM_SIZES,
  BasketMatch,
  BasketMatchPlayer,
  BasketMatchAPI,
  BasketSide,
  DEFAULT_BASKET_TEAM_SIZE,
} from "../../api/BasketMatch";
import { Option } from "../../models/generics";
import { useToasts } from "../../contexts/toasts";

// Gestione dal gestionale della partita di BASKET collegata a una prenotazione
// (speculare a FootballMatchParticipants, con in più formato, presenze e
// risultato a punti): il gestore può creare la partita, scegliere il formato
// (1v1 … 5v5), inserire/spostare/rimuovere giocatori, confermare le presenze e
// registrare il punteggio con i punti per giocatore.

type Props = {
  bookingId: string;
  usersList: Option[];
  editable: boolean;
};

const playerName = (p: BasketMatchPlayer): string => {
  if (p.guestName) return p.guestName;
  const full = `${p.user?.name ?? ""} ${p.user?.familyName ?? ""}`.trim();
  return full || "Utente sconosciuto";
};

const firstFreeSlot = (
  players: BasketMatchPlayer[],
  team: BasketSide,
  size: number
): number => {
  const used = new Set(players.filter((p) => p.team === team).map((p) => p.slot));
  for (let s = 0; s < size; s++) {
    if (!used.has(s)) return s;
  }
  // Oltre il formato: usa il primo indice non occupato successivo.
  let s = size;
  while (used.has(s)) s++;
  return s;
};

const formatOptions = BASKET_TEAM_SIZES.map((n) => ({
  label: `${n}v${n}`,
  value: n,
}));

export default function BasketMatchParticipants({
  bookingId,
  usersList,
  editable,
}: Props) {
  const { showToast } = useToasts();
  const [match, setMatch] = useState<BasketMatch | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [guest1, setGuest1] = useState("");
  const [guest2, setGuest2] = useState("");
  const [teamSize, setTeamSize] = useState<number>(DEFAULT_BASKET_TEAM_SIZE);
  // Nomi squadra in modifica (salvati con "Salva nomi").
  const [name1, setName1] = useState("");
  const [name2, setName2] = useState("");
  // Risultato in modifica: punti per giocatore (solo presenti).
  const [points, setPoints] = useState<Record<string, number>>({});
  const [resultOpen, setResultOpen] = useState(false);

  const applyMatch = useCallback((m: BasketMatch | null) => {
    setMatch(m);
    if (m) {
      setTeamSize(m.teamSize);
      setName1(m.teamName1 ?? "");
      setName2(m.teamName2 ?? "");
      setPoints(
        Object.fromEntries((m.players ?? []).map((p) => [p.id, p.points ?? 0]))
      );
    }
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    BasketMatchAPI.getByBooking(bookingId).then(
      ({ data }) => {
        applyMatch(data.result);
        setLoading(false);
      },
      () => {
        setLoading(false);
        showToast(
          "bkm-load-error",
          "Impossibile caricare la partita di basket",
          "",
          "danger"
        );
      }
    );
  }, [bookingId, showToast, applyMatch]);

  useEffect(() => {
    load();
  }, [load]);

  const players = useMemo(() => match?.players ?? [], [match]);
  const team1 = players
    .filter((p) => p.team === "team1")
    .sort((a, b) => a.slot - b.slot);
  const team2 = players
    .filter((p) => p.team === "team2")
    .sort((a, b) => a.slot - b.slot);

  const presentUserIds = useMemo(
    () => new Set(players.map((p) => p.userId).filter(Boolean) as string[]),
    [players]
  );

  const fail = useCallback(
    (key: string, text: string) => {
      setBusy(false);
      showToast(key, text, "", "danger");
    },
    [showToast]
  );

  const createMatch = useCallback(() => {
    setBusy(true);
    BasketMatchAPI.createMatch({ bookingId, teamSize }).then(
      ({ data }) => {
        applyMatch(data.result);
        setBusy(false);
      },
      () => fail("bkm-create-error", "Errore nella creazione della partita")
    );
  }, [bookingId, teamSize, applyMatch, fail]);

  const saveMeta = useCallback(
    (body: { teamName1?: string | null; teamName2?: string | null; teamSize?: number }) => {
      if (!match) return;
      setBusy(true);
      BasketMatchAPI.setMeta(match.id, body).then(
        ({ data }) => {
          applyMatch(data.result);
          setBusy(false);
        },
        () => fail("bkm-meta-error", "Errore nell'aggiornamento della partita")
      );
    },
    [match, applyMatch, fail]
  );

  const addPlayer = useCallback(
    (team: BasketSide, payload: { userId?: string; guestName?: string }) => {
      if (!match) return;
      setBusy(true);
      const slot = firstFreeSlot(players, team, match.teamSize);
      BasketMatchAPI.addPlayer(match.id, {
        team,
        slot,
        userId: payload.userId ?? null,
        guestName: payload.guestName ?? null,
      }).then(
        ({ data }) => {
          applyMatch(data.result);
          setBusy(false);
          setGuest1("");
          setGuest2("");
        },
        () => fail("bkm-add-error", "Errore nell'aggiunta del giocatore")
      );
    },
    [match, players, applyMatch, fail]
  );

  const removePlayer = useCallback(
    (playerId: string) => {
      setBusy(true);
      BasketMatchAPI.removePlayer(playerId).then(
        () => {
          load();
          setBusy(false);
        },
        () => fail("bkm-remove-error", "Errore nella rimozione del giocatore")
      );
    },
    [load, fail]
  );

  const movePlayer = useCallback(
    (player: BasketMatchPlayer) => {
      if (!match) return;
      const target: BasketSide = player.team === "team1" ? "team2" : "team1";
      const slot = firstFreeSlot(players, target, match.teamSize);
      setBusy(true);
      BasketMatchAPI.updatePlayer(player.id, { team: target, slot }).then(
        () => {
          load();
          setBusy(false);
        },
        () => fail("bkm-move-error", "Errore nello spostamento del giocatore")
      );
    },
    [match, players, load, fail]
  );

  const setPresence = useCallback(
    (playerIds: string[], confirmed: boolean) => {
      if (!playerIds.length) return;
      setBusy(true);
      BasketMatchAPI.confirmPresence({ playerIds, confirmed }).then(
        () => {
          load();
          setBusy(false);
        },
        () => fail("bkm-presence-error", "Errore nella conferma presenza")
      );
    },
    [load, fail]
  );

  const markNoShow = useCallback(
    (playerId: string) => {
      setBusy(true);
      BasketMatchAPI.markNoShow(playerId).then(
        () => {
          load();
          setBusy(false);
        },
        () => fail("bkm-noshow-error", "Errore nel segnare l'assenza")
      );
    },
    [load, fail]
  );

  // Presenti (per il risultato: solo loro possono avere punti e statistiche).
  const confirmedPlayers = useMemo(
    () => players.filter((p) => p.confirmed && !p.noShow),
    [players]
  );
  const pendingIds = useMemo(
    () => players.filter((p) => !p.confirmed && !p.noShow).map((p) => p.id),
    [players]
  );
  const pointsOf = useCallback(
    (team: BasketSide) =>
      confirmedPlayers
        .filter((p) => p.team === team)
        .reduce((a, p) => a + (points[p.id] || 0), 0),
    [confirmedPlayers, points]
  );

  const saveResult = useCallback(() => {
    if (!match) return;
    const pointsTeam1 = pointsOf("team1");
    const pointsTeam2 = pointsOf("team2");
    setBusy(true);
    BasketMatchAPI.setResult(match.id, {
      pointsTeam1,
      pointsTeam2,
      scorers: confirmedPlayers.map((p) => ({
        playerId: p.id,
        points: points[p.id] || 0,
      })),
    }).then(
      ({ data }) => {
        applyMatch(data.result);
        setBusy(false);
        setResultOpen(false);
        showToast("bkm-result-ok", "Risultato salvato", "", "success");
      },
      () => fail("bkm-result-error", "Errore nel salvataggio del risultato")
    );
  }, [match, pointsOf, confirmedPlayers, points, applyMatch, fail, showToast]);

  const availableUsers = useMemo(
    () => usersList.filter((o) => !presentUserIds.has(o.value)),
    [usersList, presentUserIds]
  );

  if (loading) {
    return (
      <div className="bm-section">
        <div className="bm-section-title">Partecipanti</div>
        <div className="d-flex justify-content-center py-2">
          <TailSpin height={20} width={20} color="#f25d0b" radius="1" />
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="bm-section">
        <div className="bm-section-title">Partecipanti</div>
        <span className="bm-empty d-block mb-2">
          Nessuna partita di basket creata per questa prenotazione.
        </span>
        {editable && (
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
            <span style={{ fontSize: 12, color: "#666" }}>Formato</span>
            <Segmented
              options={formatOptions}
              value={teamSize}
              onChange={(v) => setTeamSize(Number(v))}
              size="small"
            />
            <Button
              type="primary"
              icon={<FontAwesomeIcon icon={faBasketball} className="me-2" />}
              loading={busy}
              onClick={createMatch}
            >
              Crea partita
            </Button>
          </div>
        )}
      </div>
    );
  }

  const played = match.winnerTeam != null;

  const renderTeam = (
    team: BasketSide,
    list: BasketMatchPlayer[],
    label: string,
    guest: string,
    setGuest: (v: string) => void
  ) => (
    <div className="bm-team" style={{ flex: 1 }}>
      <span className="bm-team-label">
        {(team === "team1" ? match.teamName1 : match.teamName2) || label}
        <span style={{ color: "#999", fontWeight: 400, marginLeft: 6 }}>
          {list.length}/{match.teamSize}
        </span>
      </span>
      {list.length === 0 && <div className="bm-empty">Nessun giocatore</div>}
      {list.map((p) => (
        <div
          key={p.id}
          className="d-flex align-items-center justify-content-between bm-team-player"
          style={{ gap: 8 }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              opacity: p.noShow ? 0.5 : 1,
            }}
          >
            {editable && (
              <Checkbox
                checked={!!p.confirmed}
                disabled={busy}
                title={p.confirmed ? "Presente" : "Conferma presenza"}
                style={{ marginRight: 6 }}
                onChange={(e) => setPresence([p.id], e.target.checked)}
              />
            )}
            {playerName(p)}
            {!p.userId && (
              <span style={{ color: "#999", fontSize: 11, marginLeft: 6 }}>
                Ospite
              </span>
            )}
            {p.noShow && (
              <Tag color="red" style={{ marginLeft: 6, fontSize: 10 }}>
                Assente
              </Tag>
            )}
            {!!p.points && (
              <Tag color="orange" style={{ marginLeft: 6, fontSize: 10 }}>
                {p.points} pt
              </Tag>
            )}
          </span>
          {editable && (
            <span style={{ whiteSpace: "nowrap" }}>
              {!p.noShow && (
                <Button
                  type="text"
                  size="small"
                  disabled={busy}
                  title="Segna come assente (riduce l'affidabilità)"
                  onClick={() => markNoShow(p.id)}
                  icon={<FontAwesomeIcon icon={faUserSlash} />}
                />
              )}
              <Button
                type="text"
                size="small"
                disabled={busy}
                title="Sposta nell'altra squadra"
                onClick={() => movePlayer(p)}
                icon={<FontAwesomeIcon icon={faRightLeft} />}
              />
              <Button
                type="text"
                size="small"
                danger
                disabled={busy}
                title="Rimuovi dalla partita"
                onClick={() => removePlayer(p.id)}
                icon={<FontAwesomeIcon icon={faUserMinus} />}
              />
            </span>
          )}
        </div>
      ))}

      {editable && (
        <div style={{ marginTop: 8 }}>
          <Select
            showSearch
            style={{ width: "100%" }}
            placeholder="Aggiungi giocatore"
            value={null as any}
            options={availableUsers}
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            onSelect={(value: any) => addPlayer(team, { userId: value })}
            getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
          />
          <div className="d-flex" style={{ gap: 6, marginTop: 6 }}>
            <Input
              placeholder="Nome ospite"
              value={guest}
              size="small"
              onChange={(e) => setGuest(e.target.value)}
              onPressEnter={() => {
                if (guest.trim()) addPlayer(team, { guestName: guest.trim() });
              }}
            />
            <Button
              size="small"
              disabled={busy || !guest.trim()}
              icon={<FontAwesomeIcon icon={faUserPlus} />}
              onClick={() => {
                if (guest.trim()) addPlayer(team, { guestName: guest.trim() });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderResultEditor = () => (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        border: "1px solid #eee",
        borderRadius: 10,
        background: "#fafafa",
      }}
    >
      <div className="d-flex align-items-center justify-content-between mb-2">
        <strong style={{ fontSize: 13 }}>Risultato e punti per giocatore</strong>
        <strong style={{ fontSize: 18, color: "#f25d0b" }}>
          {pointsOf("team1")} - {pointsOf("team2")}
        </strong>
      </div>
      {confirmedPlayers.length === 0 ? (
        <div className="bm-empty">
          Conferma prima la presenza dei giocatori per assegnare i punti.
        </div>
      ) : (
        <div className="d-flex" style={{ gap: 16 }}>
          {(["team1", "team2"] as BasketSide[]).map((team) => (
            <div key={team} style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                {(team === "team1" ? match.teamName1 : match.teamName2) ||
                  (team === "team1" ? "Squadra 1" : "Squadra 2")}
              </div>
              {confirmedPlayers
                .filter((p) => p.team === team)
                .map((p) => (
                  <div
                    key={p.id}
                    className="d-flex align-items-center justify-content-between"
                    style={{ gap: 8, marginBottom: 4 }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: 13,
                      }}
                    >
                      {playerName(p)}
                    </span>
                    <InputNumber
                      size="small"
                      min={0}
                      max={200}
                      value={points[p.id] ?? 0}
                      style={{ width: 70 }}
                      onChange={(v) =>
                        setPoints((prev) => ({ ...prev, [p.id]: Number(v) || 0 }))
                      }
                    />
                  </div>
                ))}
            </div>
          ))}
        </div>
      )}
      <div className="d-flex justify-content-end mt-2" style={{ gap: 8 }}>
        <Button size="small" onClick={() => setResultOpen(false)}>
          Chiudi
        </Button>
        <Button
          size="small"
          type="primary"
          disabled={busy || confirmedPlayers.length === 0}
          icon={<FontAwesomeIcon icon={faFloppyDisk} className="me-1" />}
          onClick={saveResult}
        >
          Salva risultato
        </Button>
      </div>
    </div>
  );

  return (
    <div className="bm-section">
      <div className="bm-section-title">
        Partecipanti
        <span style={{ color: "#999", fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
          Basket {match.teamSize}v{match.teamSize}
          {played
            ? ` · Risultato ${match.pointsTeam1 ?? 0} - ${match.pointsTeam2 ?? 0}`
            : ""}
        </span>
      </div>

      {editable && (
        <div
          className="d-flex align-items-center flex-wrap mb-2"
          style={{ gap: 8 }}
        >
          <span style={{ fontSize: 12, color: "#666" }}>Formato</span>
          <Segmented
            options={formatOptions}
            value={match.teamSize}
            disabled={busy}
            size="small"
            onChange={(v) => saveMeta({ teamSize: Number(v) })}
          />
          <Input
            size="small"
            placeholder="Nome squadra 1"
            value={name1}
            maxLength={24}
            style={{ width: 140 }}
            onChange={(e) => setName1(e.target.value)}
          />
          <Input
            size="small"
            placeholder="Nome squadra 2"
            value={name2}
            maxLength={24}
            style={{ width: 140 }}
            onChange={(e) => setName2(e.target.value)}
          />
          <Button
            size="small"
            disabled={
              busy ||
              (name1 === (match.teamName1 ?? "") && name2 === (match.teamName2 ?? ""))
            }
            onClick={() =>
              saveMeta({
                teamName1: name1.trim() || null,
                teamName2: name2.trim() || null,
              })
            }
          >
            Salva nomi
          </Button>
        </div>
      )}

      <div className="bm-teams" style={{ alignItems: "flex-start", gap: 16 }}>
        {renderTeam("team1", team1, "Squadra 1", guest1, setGuest1)}
        <div className="bm-vs">VS</div>
        {renderTeam("team2", team2, "Squadra 2", guest2, setGuest2)}
      </div>

      {editable && players.length > 0 && (
        <div className="d-flex align-items-center flex-wrap mt-2" style={{ gap: 8 }}>
          <Button
            size="small"
            disabled={busy || pendingIds.length === 0}
            icon={<FontAwesomeIcon icon={faCheckDouble} className="me-1" />}
            onClick={() => setPresence(pendingIds, true)}
          >
            Conferma tutte le presenze
            {pendingIds.length > 0 ? ` (${pendingIds.length})` : ""}
          </Button>
          <Button
            size="small"
            type={resultOpen ? "default" : "primary"}
            disabled={busy}
            icon={<FontAwesomeIcon icon={faBasketball} className="me-1" />}
            onClick={() => setResultOpen((o) => !o)}
          >
            {played ? "Modifica risultato" : "Inserisci risultato"}
          </Button>
        </div>
      )}

      {editable && resultOpen && renderResultEditor()}
    </div>
  );
}
